import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Paperclip, AlertTriangle, Users, UserPlus, X } from "lucide-react";
import { getQualityMeta, createQualityMeta, updateQualityMeta } from "../../../api/quality/meta";
import MobileAttachmentBox from "../../../components/common/papers/MobileAttachmentBox";
import MobileReferenceModal from "../../../components/approval/MobileReferenceModal";
import { getDocPersons } from "../../../api/papers";

// 부적합 형태 옵션
const NC_TYPE_OPTIONS = [
  { value: "", label: "선택" },
  { value: "규격미달", label: "규격미달" },
  { value: "외관불량", label: "외관불량" },
  { value: "치수허용초과", label: "치수허용초과" },
  { value: "문서미비", label: "문서미비" },
  { value: "공정미준수", label: "공정미준수" },
  { value: "기타", label: "기타" },
];

export default function NcrNew() {
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const [dns, setDns] = useState(doc_dns || "");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const attachmentRef = useRef(null);

  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";

  // 조치 담당자
  const [requestPeople, setRequestPeople] = useState([]);
  const [peopleModalOpen, setPeopleModalOpen] = useState(false);
  const [tempSelectedIds, setTempSelectedIds] = useState([]);

  // 상단 공통 폼
  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    title: "",
    note: "",
    result: "",
    proposer: "",
  });

  // NCR 메타
  const [ncr, setNcr] = useState({
    location: "",
    vendor: "",
    action_dept: "",
    has_attachment: "no",
    nc_type: "",
    description: "",
    immediate_action: "",
  });

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    docInfo: true,
    ncrInfo: true,
    people: true,
    description: true,
    action: false,
    attachment: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const onForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onNcr = (k, v) => setNcr((p) => ({ ...p, [k]: v }));

  // 자동 제목
  const autoTitle = useMemo(() => {
    const t = ncr.nc_type?.trim();
    const l = ncr.location?.trim();
    if (t && l) return `[NCR] ${t} - ${l}`;
    if (l) return `[NCR] ${l}`;
    if (t) return `[NCR] ${t}`;
    return "";
  }, [ncr.nc_type, ncr.location]);

  // 기존 문서 로드
  useEffect(() => {
    if (!doc_dns) return;

    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns: doc_dns });
        const meta = res?.meta || {};
        if (meta.form) {
          setForm((prev) => ({ ...prev, ...meta.form }));
        }
        if (meta.ncrMeta) {
          setNcr((prev) => ({ ...prev, ...meta.ncrMeta }));
        }
        
        // 담당자 로드
        try {
          const people = await getDocPersons(doc_dns);
          const mapped = people.map((p) => ({
            id: p.user.id,
            name: p.user.name || `${p.user.last_name ?? ""}${p.user.first_name ?? ""}`,
          }));
          setRequestPeople(mapped);
        } catch (e) {
          console.error("담당자 로드 실패:", e);
        }
      } catch (err) {
        console.error(err);
        alert("NCR 데이터를 불러오지 못했습니다.");
      }
    }
    loadDetail();
  }, [doc_dns]);

  // 담당자 모달 열기
  const openPeopleModal = () => {
    setTempSelectedIds(requestPeople.map((p) => p.id));
    setPeopleModalOpen(true);
  };

  // 담당자 저장
  const handlePeopleSave = (ids, userDetails) => {
    const mapped = userDetails.map((u) => ({
      id: u.id,
      name: u.name ?? `${u.last_name ?? ""}${u.first_name ?? ""}`,
    }));
    setRequestPeople(mapped);
    setPeopleModalOpen(false);
    return true;
  };

  // 담당자 제거
  const removePerson = (id) => {
    setRequestPeople((prev) => prev.filter((p) => p.id !== id));
  };

  // 저장 처리
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const body = {
        layout: null,
        values: {
          project: projectId,
          module: "quality",
          doc_type: "NC",
          dns,
          meta: {
            form: {
              ...form,
              title: form.title?.trim() || autoTitle,
            },
            ncrMeta: ncr,
            __client__: { form_type: "NC" },
          },
        },
      };

      let result;
      if (dns) {
        result = await updateQualityMeta(dns, body.values);
      } else {
        result = await createQualityMeta(body.values);
      }

      const newDns = result?.dns || result?.doc_dns || dns;

      // 첨부파일 저장
      if (newDns && attachmentRef.current) {
        await attachmentRef.current.save(newDns);
      }

      if (newDns) {
        setDns(newDns);
        alert("저장 완료!");
        navigate(`/project/${projectId}/quality/ncr/${encodeURIComponent(newDns)}/view`);
      } else {
        alert("저장 완료!");
        navigate(BOARD);
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const BOARD = `/project/${projectId}/quality/list/all`;
  const handleBack = () => navigate(BOARD);

  // 상태 텍스트
  const getStatusText = (status) => {
    const map = {
      draft: "작성중",
      in_progress: "조치중",
      pending_verify: "검증대기",
      closed: "종결",
    };
    return map[status] || status;
  };

  // ------------------------------
  // 렌더링
  // ------------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 모바일 본문 영역 */}
      <main className="px-4 pt-4 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">품질</p>
          <h2 className="text-xl font-bold text-gray-900">부적합보고서 (NCR)</h2>
        </header>

        {/* 요약 카드 */}
        <div className="mb-4 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-blue-600" />
              <span className="text-xs font-medium text-blue-800">부적합 보고</span>
            </div>
            <div className="flex items-center gap-2">
              {form.result && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  form.result === 'pass' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {form.result === 'pass' ? '적합' : '부적합'}
                </span>
              )}
              <span className="text-xs text-blue-600">{form.doc_date}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {ncr.nc_type || "-"}
              </div>
              <div className="text-[10px] text-gray-500">부적합형태</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {ncr.location || "-"}
              </div>
              <div className="text-[10px] text-gray-500">발생장소</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-white/80">
              <div className="text-sm font-bold text-gray-800 truncate px-1">
                {getStatusText(form.status)}
              </div>
              <div className="text-[10px] text-gray-500">상태</div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* 1. 문서 정보 */}
          <Section title="문서 정보" expanded={expandedSections.docInfo} onToggle={() => toggleSection("docInfo")}>
            <InputField
              label="제목"
              value={form.title}
              onChange={(v) => onForm("title", v)}
              placeholder={autoTitle || "문서 제목"}
            />
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="발생일"
                type="date"
                value={form.doc_date}
                onChange={(v) => onForm("doc_date", v)}
                required
              />
              <div>
                <label className="text-gray-500 text-xs mb-1 block">상태</label>
                <select
                  value={form.status}
                  onChange={(e) => onForm("status", e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
                >
                  <option value="draft">작성중</option>
                  <option value="in_progress">조치중</option>
                  <option value="pending_verify">검증대기</option>
                  <option value="closed">종결</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">최종 판정</label>
                <select
                  value={form.result}
                  onChange={(e) => onForm("result", e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
                >
                  <option value="">선택</option>
                  <option value="pass">적합</option>
                  <option value="fail">부적합</option>
                </select>
              </div>
              <InputField
                label="제안자"
                value={form.proposer}
                onChange={(v) => onForm("proposer", v)}
                placeholder="이름 / 부서"
                required
              />
            </div>
          </Section>

          {/* 2. 부적합 정보 */}
          <Section title="부적합 정보" expanded={expandedSections.ncrInfo} onToggle={() => toggleSection("ncrInfo")}>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">
                부적합형태 <span className="text-red-500">*</span>
              </label>
              <select
                value={ncr.nc_type}
                onChange={(e) => onNcr("nc_type", e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
              >
                {NC_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <InputField
              label="발생장소"
              value={ncr.location}
              onChange={(v) => onNcr("location", v)}
              placeholder="예: B2 슬래브"
              required
            />
            <div className="grid grid-cols-2 gap-2">
              <InputField
                label="업체"
                value={ncr.vendor}
                onChange={(v) => onNcr("vendor", v)}
                placeholder="시공/납품 업체"
              />
              <InputField
                label="조치부서"
                value={ncr.action_dept}
                onChange={(v) => onNcr("action_dept", v)}
                placeholder="예: 건품팀"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">첨부파일 유무</label>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={ncr.has_attachment === "yes"}
                    onChange={() => onNcr("has_attachment", "yes")}
                    className="w-4 h-4 text-blue-500"
                  />
                  <span className="text-sm">유</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={ncr.has_attachment === "no"}
                    onChange={() => onNcr("has_attachment", "no")}
                    className="w-4 h-4 text-blue-500"
                  />
                  <span className="text-sm">무</span>
                </label>
              </div>
            </div>
          </Section>

          {/* 3. 조치 담당자 */}
          <Section 
            title="조치 담당자" 
            expanded={expandedSections.people} 
            onToggle={() => toggleSection("people")}
            icon={<Users size={16} className="text-gray-500" />}
            badge={requestPeople.length > 0 ? `${requestPeople.length}명` : null}
          >
            {/* 선택된 담당자 목록 */}
            {requestPeople.length > 0 ? (
              <div className="space-y-2">
                {requestPeople.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-blue-50 border border-blue-100"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {(person.name || "?").charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-gray-800">{person.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePerson(person.id)}
                      className="p-1.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">선택된 담당자가 없습니다</p>
            )}

            {/* 담당자 추가 버튼 */}
            <button
              type="button"
              onClick={openPeopleModal}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <UserPlus size={18} />
              <span className="text-sm font-medium">담당자 선택</span>
            </button>

            {/* 담당자 선택 모달 */}
            <MobileReferenceModal
              isOpen={peopleModalOpen}
              onClose={() => setPeopleModalOpen(false)}
              selectedIds={tempSelectedIds}
              name="조치 담당자"
              onChange={(ids) => setTempSelectedIds(ids)}
              onSave={handlePeopleSave}
            />
          </Section>

          {/* 4. 부적합 내용 */}
          <Section title="부적합 내용" expanded={expandedSections.description} onToggle={() => toggleSection("description")}>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">
                부적합 내용 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={ncr.description}
                onChange={(e) => onNcr("description", e.target.value)}
                placeholder="발생 경위, 기준 대비 차이, 사진 설명 등"
                rows={5}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
          </Section>

          {/* 5. 즉시/임시조치 */}
          <Section title="즉시/임시조치" expanded={expandedSections.action} onToggle={() => toggleSection("action")}>
            <textarea
              value={ncr.immediate_action}
              onChange={(e) => onNcr("immediate_action", e.target.value)}
              placeholder="예: 불합격품 격리, 임시조치 사항 등"
              rows={4}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none resize-none"
            />
          </Section>

          {/* 6. 첨부자료 */}
          <Section 
            title="첨부자료" 
            expanded={expandedSections.attachment} 
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentRef} />
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            onClick={handleBack}
          >
            목록
          </button>
          <button
            type="button"
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="p-4 space-y-3">{children}</div>
      )}
    </section>
  );
}

// 입력 필드 컴포넌트
function InputField({ label, value, onChange, type = "text", placeholder, disabled, required }) {
  return (
    <div>
      <label className="text-gray-500 text-xs mb-1 block">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  );
}
